import _ from 'lodash';
import store from 'store/index';
import moment from 'moment';
import Api from 'services/Api';
import Validator from 'services/Validator';
import {requestPayload} from 'store/modules/claim';
import {validDate} from 'components/form/validator/validations';
import {defaultIncomePeriodTemplate, personTemplate} from 'store/modules/shared/templates';
import {
    calcAgeDeath,
    determineLife,
    doesIncomePeriodNeedToUpdate,
    dynamicUpdates,
    findNextPeriodEnd,
    findNextPeriodStart,
    incomeValid,
    isValidIncome,
    removePeriodsSplitForTheRetired,
    splitPeriodsForRetirement,
    throttledIncome,
    updateIncomePeriodsForRetirement,
    updateObject,
    userSettable,
    zeroCheck
} from 'services/personFunctions';
import {previousValid} from 'store/generators/step';

export const mutationTypes = {
    RESET: 'RESET',
    UPDATE: 'UPDATE',
    UPDATE_DECEASED: 'UPDATE_DECEASED',
    SERVER_UPDATE_INCOME: 'SERVER_UPDATE_INCOME',
    UPDATE_DECEASED_INCOME: 'UPDATE_DECEASED_INCOME',
    SERVER_UPDATE_DECEASED: 'SERVER_UPDATE_DECEASED',
    ADD_DECEASED_INCOME_PERIOD: 'ADD_DECEASED_INCOME_PERIOD',
    UPDATE_DECEASED_INCOME_PERIOD: 'UPDATE_DECEASED_INCOME_PERIOD',
    DELETE_DECEASED_INCOME_PERIOD: 'DELETE_DECEASED_INCOME_PERIOD',
    UPDATE_DECEASED_LIFE_EXPECTANCY: 'UPDATE_DECEASED_LIFE_EXPECTANCY',
    UPDATING_DECEASED_INCOME_PERIOD: 'UPDATING_DECEASED_INCOME_PERIOD',
};

// Initial State
const state = {
    theDeceased: personTemplate(),
    periodDeathToSettlement: null,
    ageAtSettlement: null,
    updatingDeceasedIncome: false,
};

/**
 * Validation Rules.
 *
 * @type {Object}
 */
let rules = {
    'theDeceased.name': {type: 'text', rules: 'required'},
    'theDeceased.gender': {type: 'text', rules: 'required'},
    'theDeceased.dateOfBirth': {type: 'date', rules: 'required'},
    'theDeceased.onsetOfSymptoms': {type: 'date', rules: 'required'},
    'theDeceased.dateOfDeath': {type: 'date', rules: 'required'},
};

// Getters
const getters = {
    value: state => key => state[key],
    theDeceased: state => state.theDeceased,
    theDeceasedIncome: state => state.theDeceased.income,
    theDeceasedLifeExpectancy: state => state.theDeceased.lifeExpectancy,
    theDeceasedIncomeValid: state => {
        let assumedSettlement = moment(store.getters['fatal/dependency/proceduralDates/date']('dateOfAssumedSettlement'), 'DD/MM/YYYY');

        return incomeValid(assumedSettlement, state.theDeceased);
    },
    getDeceasedIncomePeriod: state => index => state.theDeceased.income.periods[index],
    theDeceasedNetIncome: state => state.theDeceased.income.netIncome + state.theDeceased.income.netPensionIncome,
    theDeceasedGrossIncome: state => state.theDeceased.income.grossIncome + state.theDeceased.income.grossPensionIncome,
    durationDeceasedDependency: state => state.theDeceased.lifeExpectancy.ageAtDeath - state.theDeceased.ageAtDeath,
    getDeceasedIncomeStatus: state => state.updatingDeceasedIncome,
};

/**
 * Define the update actions in a functional way to digest by deceased
 * Curried function configured for when deceased values change
 *
 * @type {function(key, dispatch, person, index):Array.<Promise>}
 */
const theDeceasedEvents = dynamicUpdates({
    ageAtDeath: 'updateTheDeceasedValue',
    lifeExpectancy: 'updateTheDeceasedLifeExpectancy',
    incomeValue: 'updateTheDeceasedIncomeValue',
    retirementDate: 'updateRetirementAgeAndDate',
});

/**
 * Throttle update of person life expectancy to allow for typing
 *
 * @param dispatch
 * @param action
 * @param person
 * @param lifeExpectancy
 * @param index
 */
export const throttleUpdateLife = _.debounce((dispatch, action, person, lifeExpectancy, index) => {
    if (validDate(person.dateOfBirth)) {
        let assumedSettlement = store.getters['fatal/dependency/claimDetails/value']('assumedSettlement');
        determineLife(assumedSettlement, person, lifeExpectancy)
            .then(updatedLife => updateObject(dispatch, action, updatedLife, index))
            .then(() => dispatch('updatePeriodsForLifeExpectancy'));
    }
}, 500);

// Actions
const actions = {
    /**
     * Save the step by submitting to the server.
     *
     * @param dispatch
     * @param payload
     */
    save({dispatch}, payload) {
        // We might change this to something which
        // is a bit cleaner...
        payload.data = state;

        Api.claims.save(payload).then((response) => {
            return response;
        });

        store.dispatch('fatal/dependency/save', requestPayload(0));
    },

    /**
     * Loads the step being received from the Server, to be placed in the state.
     *
     * @param commit
     * @param step
     */
    load({commit}, step) {
        const data = _.deeply(_.mapKeys)(step, (value, key) => _.camelCase(key)).data;

        commit(mutationTypes.SERVER_UPDATE_DECEASED, data.theDeceased);
    },

    /**
     * Performs any processing once the data from the server has been stored.
     * This is a good place to do calculations or dispatch other actions which
     * are used to refresh/update calculated data on other steps/components.
     *
     * @param dispatch
     */
    process({dispatch, rootState}) {
        let updatedRules = _.clone(rules);
        if (rootState.user.user.hideNames) {
            delete updatedRules['theDeceased.name'];
        }
        store.dispatch('fatal/dependency/step/setStepNumberValid', {
            stepNo: 1,
            valid: previousValid(rootState, 1) && (new Validator(state, updatedRules)).validate(),
        });

        dispatch('determineAgeOfDeathToSettlement');
        dispatch('determineAgeAtSettlement');
    },

    /**
     * Validate the step.
     *
     * @param commit
     */
    validate({rootState}) {
        let updateRules = _.clone(rules);
        if (rootState.user.user.hideNames) {
            delete updateRules['theDeceased.name'];
        }
        store.dispatch('fatal/dependency/step/setStepNumberValid', {
            stepNo: 1,
            valid: previousValid(rootState, 1) && (new Validator(state, updateRules)).validate(),
        });
    },

    /**
     * Resets the step back to defaults
     *
     * @param commit
     */
    reset({commit}) {
        store.dispatch('fatal/dependency/step/setStepNumberValid', {
            stepNo: 1,
            valid: false,
        });

        commit(mutationTypes.RESET);
    },

    /**
     * Update single value
     *
     * @param commit
     * @param key
     * @param value
     */
    update({commit}, {key, value}) {
        commit(mutationTypes.UPDATE, {key, value});
    },

    /**
     * Update the deceased LifeExpectancy
     *
     * @param dispatch
     * @param state
     * @param lifeExpectancy
     */
    updateTheDeceasedLifeExpectancy({dispatch, state}, {lifeExpectancy}) {
        return updateObject(dispatch, 'updateTheDeceasedLifeExpectancyValue', lifeExpectancy)
            .then(() => throttleUpdateLife(dispatch, 'updateTheDeceasedLifeExpectancyValue', state.theDeceased, lifeExpectancy));
    },

    /**
     * Update the deceased LifeExpectancy store
     *
     * @param state
     * @param commit
     * @param dispatch
     * @param key
     * @param value
     */
    updateTheDeceasedLifeExpectancyValue({commit, state, dispatch}, {key, value}) {
        if (state.theDeceased.lifeExpectancy[key] !== value) {
            commit(mutationTypes.UPDATE_DECEASED_LIFE_EXPECTANCY, {key, value});
        }
    },

    /**
     * Update the deceased income value
     *
     * @param state
     * @param commit
     * @param key
     * @param value
     */
    updateTheDeceasedIncomeValue({commit, state}, {key, value}) {
        if (state.theDeceased.income[key] === value || (typeof value === 'number' && isNaN(value))) {

            return;
        }
        commit(mutationTypes.UPDATE_DECEASED_INCOME, {key, value});
        return key;
    },

    /**
     * Update the retirement date
     *
     * @param state
     * @param commit
     */
    updateRetirementAgeAndDate: _.debounce(({state, commit, dispatch}) => {
        if (state.theDeceased.income.retirementAgeCalc === 'auto') {
            let previousRetirement = state.theDeceased.income.retirementDate;
            return Api.calculators.dates.statePensionAge(state.theDeceased)
                .then((data) => {
                    commit(mutationTypes.UPDATE_DECEASED_INCOME, {
                        key: 'retirementAge',
                        value: data.statePension.retirementAge
                    });
                    commit(mutationTypes.UPDATE_DECEASED_INCOME, {
                        key: 'retirementDate',
                        value: data.statePension.retirementDate
                    });
                    dispatch('updateIncomePeriodForRetirementChange', previousRetirement);
                });
        }
    }, 500),

    /**
     * Split dependant periods and adjust those splits depending on date of retirement.
     *
     * @param state
     * @param commit
     * @param {string} [previousRetirementDate] previous retirement date.
     */
    updateIncomePeriodForRetirementChange({state, commit}, previousRetirementDate) {
        if (state.theDeceased.income.retired) {
            return;
        }

        let retirementDate = state.theDeceased.income.retirementDate;
        let periods = state.theDeceased.income.periods;
        let end = periods[periods.length - 1].endOfPeriod;
        splitPeriodsForRetirement(
            commit,
            mutationTypes.UPDATE_DECEASED_INCOME_PERIOD,
            mutationTypes.ADD_DECEASED_INCOME_PERIOD,
            previousRetirementDate,
            retirementDate,
            periods,
            end
        );
    },

    /**
     * Remove any periods split for retirement.
     *
     * @param state
     * @param dispatch
     * @param previousRetirementDate
     * @return {Promise}
     */
    removePeriodsSplitForTheRetired({state, dispatch}, previousRetirementDate) {
        let retirementDate = state.theDeceased.income.retirementDate;
        let periods = state.theDeceased.income.periods;
        if (!state.theDeceased.income.retired) {
            return;
        }
        return removePeriodsSplitForTheRetired(
            dispatch,
            'deleteDeceasedIncomePeriod',
            periods,
            previousRetirementDate,
            retirementDate
        );
    },

    updateIncomePeriodsForRetirement({state, dispatch}) {
        let deceased = state.theDeceased;
        let periods = deceased.income.periods;
        return updateIncomePeriodsForRetirement(
            dispatch,
            'updateDeceasedIncomePeriodValue',
            periods,
            deceased.income.retired
        ).then((updatedPeriodIndex) => {
            let proms = updatedPeriodIndex.map((updated, index) => {
                if (!updated) return;
                return zeroCheck(dispatch, index, periods[index], deceased, 'updateDeceasedIncomePeriodValue', 'deceased')
                    .then(() => throttledIncome(periods[index], dispatch, 'updateDeceasedIncomePeriodValue', {index}, deceased));
            });
            return Promise.all(proms);
        });
    },

    /**
     * Update income periods for the deceased and dependants when the DOD has changed
     *
     * @param dispatch
     * @param state
     * @param commit
     */
    updatePeriodsForLifeExpectancy({state, rootState, dispatch}) {
        if (
            !rootState.fatal.dependency.step.steps[0].valid
            || !rootState.fatal.dependency.step.steps[1].valid
            || !rootState.fatal.dependency.step.steps[2].valid
            || !rootState.fatal.dependency.step.steps[3].valid
        ) {
            return;
        }

        let dateOfDeath = state.theDeceased.lifeExpectancy.dateOfDeath;
        let mDateOfDeath = moment(dateOfDeath, 'DD/MM/YYYY');
        let lastIndex = state.theDeceased.income.periods.length - 1;

        let proms = state.theDeceased.income.periods.map((period, index) => {
            let start = moment(period.startOfPeriod, 'DD/MM/YYYY');
            let end = moment(period.endOfPeriod, 'DD/MM/YYYY');
            // If it is now after the date of death
            if (start.isAfter(mDateOfDeath)) {
                // Remove it
                return dispatch('deleteDeceasedIncomePeriod', {index});
            }
            // If it is the last period or end after date of death, shift it back.
            else if (end.isAfter(mDateOfDeath) || index === lastIndex) {
                return dispatch('updateDeceasedIncomePeriodValue', {index, key: 'endOfPeriod', value: dateOfDeath});
            }
        });
        proms.push(dispatch('updateIncomePeriodForRetirementChange'));
        proms.push(dispatch('fatal/dependency/dependants/updatePeriodsForLifeExpectancy', dateOfDeath, {root: true}));
        proms.push(dispatch('fatal/dependency/updateTheMultiplicand', null, {root: true}));
        return Promise.all(proms);
    },

    /**
     * Determine age of death to settlement
     *
     * @param rootGetters
     * @param state
     * @param commit
     */
    determineAgeOfDeathToSettlement({commit, state, rootGetters}) {
        const mSettleDate = moment(rootGetters['fatal/dependency/claimDetails/value']('assumedSettlement'), 'DD/MM/YYYY');
        const mDod = moment(state.theDeceased.dateOfDeath, 'DD/MM/YYYY');
        if (mSettleDate.isValid() && mDod.isValid()) {
            commit(mutationTypes.UPDATE,
                {key: 'periodDeathToSettlement', value: Math.round(mSettleDate.diff(mDod, 'years', true) * 10) / 10});
        }
    },

    /**
     * Determine age at settlement
     *
     * @param rootGetters
     * @param state
     * @param commit
     */
    determineAgeAtSettlement({commit, state, rootGetters}) {
        const mSettleDate = moment(rootGetters['fatal/dependency/claimDetails/value']('assumedSettlement'), 'DD/MM/YYYY');
        const mDob = moment(state.theDeceased.dateOfBirth, 'DD/MM/YYYY');
        if (mSettleDate.isValid() && mDob.isValid()) {
            commit(mutationTypes.UPDATE,
                {key: 'ageAtSettlement', value: Math.round(mSettleDate.diff(mDob, 'years', true) * 10) / 10});
        }
    },

    /**
     * Update all values on the deceased
     *
     * @param dispatch
     * @param theDeceased
     * @return {Promise}
     */
    updateTheDeceased({dispatch}, theDeceased) {
        return updateObject(dispatch, 'updateTheDeceasedValue', theDeceased);
    },

    /**
     * Update a single deceased value
     *
     * Triggers defendant update on ../fatal store and
     *
     * @param {function} commit
     * @param {state} state
     * @param {function} dispatch
     * @param key
     * @param value
     * @param force
     */
    updateTheDeceasedValue({commit, state, dispatch}, {key, value, force = false}) {
        if (state.theDeceased[key] !== value && (force || userSettable(key))) {
            commit(mutationTypes.UPDATE_DECEASED, {key, value});
            //Update other values that rely on deceased values
            return Promise.all([
                ...theDeceasedEvents(key, dispatch, state.theDeceased),
                dispatch('determineAgeOfDeathToSettlement'),
                dispatch('determineAgeAtSettlement'),
                dispatch('fatal/dependency/dependantUpdate', key, {root: true}),
            ]);
        }
    },

    /**
     * Add an income period to the deceased
     *
     * @param commit
     * @param state
     */
    addDeceasedIncomePeriod({commit, state}) {
        let periods = state.theDeceased.income.periods,
            start = findNextPeriodStart(periods, state.theDeceased.dateOfDeath),
            end = findNextPeriodEnd(periods, state.theDeceased.lifeExpectancy.dateOfDeath);

        commit(mutationTypes.ADD_DECEASED_INCOME_PERIOD, defaultIncomePeriodTemplate({
            start: start,
            end: end,
        }));
    },

    /**
     * Update the deceased's income period
     *
     * @param dispatch
     * @param index
     * @param period
     * @param deceased
     */
    updateDeceasedIncomePeriod({state, commit, dispatch}, {index, period, deceased}) {
        if (!state.updatingDeceasedIncome) {
            commit(mutationTypes.UPDATING_DECEASED_INCOME_PERIOD);
        }

        updateObject(dispatch, 'updateDeceasedIncomePeriodValue', period, index)
            .then(updatedKeys => {
                // if does not need to update, or is not valid or something else?
                if (!doesIncomePeriodNeedToUpdate(updatedKeys) ||
                    !isValidIncome(period)
                    || deceased.income.retirementAge < 50
                    || deceased.income.retirementAge === '') {
                    return;
                }

                return Promise.all([
                    zeroCheck(dispatch, index, period, deceased, 'updateDeceasedIncomePeriodValue', 'deceased')
                        .then(() => {
                            dispatch('fatal/dependency/dependencyOnIncome/resetMultiplier', null, {root: true});
                        }),

                    throttledIncome(period, dispatch, 'updateDeceasedIncomePeriodValue', {index}, deceased)
                ]);
            }).then(() => {
            if (state.updatingDeceasedIncome) {
                commit(mutationTypes.UPDATING_DECEASED_INCOME_PERIOD);
            }
        });
    },

    /**
     * Update the deceased's income period values
     *
     * @param commit
     * @param state
     * @param index
     * @param key
     * @param value
     */
    updateDeceasedIncomePeriodValue({commit, state}, {index, key, value}) {
        if (state.theDeceased.income.periods[index]) {
            let currentPeriod = state.theDeceased.income.periods[index];
            if (currentPeriod[key] === value) {
                return;
            }
            commit(mutationTypes.UPDATE_DECEASED_INCOME_PERIOD, {index, key, value});
            return key;
        }
    },

    /**
     * Delete a deceased's income period
     *
     * @param commit
     * @param state
     * @param dispatch
     * @param index
     */
    deleteDeceasedIncomePeriod({commit, state, dispatch}, {index}) {
        //Grab the end of period
        const periodEnd = state.theDeceased.income.periods[index].endOfPeriod;
        commit(mutationTypes.DELETE_DECEASED_INCOME_PERIOD, index);
        dispatch('fatal/dependency/updateTheMultiplicand', null, {root: true});

        // If the periods are now nothing
        if (!state.theDeceased.income.periods.length) {
            return dispatch('addDeceasedIncomePeriod');
        }
        // If we have a previous period
        if (state.theDeceased.income.periods[index - 1]) {
            // The end of the previous period should now be the end of the deleted period.
            return dispatch('updateDeceasedIncomePeriodValue', {
                key: 'endOfPeriod',
                value: periodEnd,
                index: index - 1
            });
        }
    },

    /**
     * Remove a deceased's invalid income period
     *
     * @param commit
     * @param state
     * @param dispatch
     */
    removeDeceasedInvalidPeriodAndAddDefaults({commit, state, dispatch}) {
        let dateOfDeath = moment(state.theDeceased.dateOfDeath, 'DD/MM/YYYY');
        let retired = state.theDeceased.income.retired;

        // Remove the invalid periods
        state.theDeceased.income.periods.forEach(function (period, index) {
            let startOfPeriod = moment(period.startOfPeriod, 'DD/MM/YYYY');
            if (retired) {
                commit(mutationTypes.UPDATE_DECEASED_INCOME_PERIOD, {index, key: 'employmentIncome', value: 0});
                dispatch('fatal/dependency/dependencyOnIncome/resetMultiplier', null, {root: true});
                throttledIncome(period, dispatch, 'updateDeceasedIncomePeriodValue', {index}, state.theDeceased);
            }

            if (startOfPeriod.isBefore(dateOfDeath)) {
                dispatch('deleteDeceasedIncomePeriod', {index});
            }
        });
    },

    updateDeceasedIncome({dispatch, state}, {income}) {
        let prevRetirementDate = state.theDeceased.income.retirementDate;
        // Resolve all changed values
        return updateObject(dispatch, 'updateTheDeceasedIncomeValue', income).then(keys => {
            if (~keys.indexOf('retirementDate') || (~keys.indexOf('retired') && !income.retired)) {
                return dispatch('updateIncomePeriodForRetirementChange', prevRetirementDate);
            }
            if (~keys.indexOf('retired') && income.retired) {
                return dispatch('removePeriodsSplitForTheRetired', prevRetirementDate)
                    .then(() => dispatch('updateIncomePeriodsForRetirement'));
            }
        });
    },

    serverUpdateDeceasedIncome({commit}, {data}) {
        commit(mutationTypes.SERVER_UPDATE_INCOME, data.deceased);
    },

    /**
     * Calculate loss of earnings
     *
     * @return promise
     */
    calculateLossOfEarnings: _.debounce(({dispatch, state, rootGetters}) => {
        let deceased = state.theDeceased;
        const mSettleDate = moment(rootGetters['fatal/dependency/claimDetails/value']('assumedSettlement'), 'DD/MM/YYYY');
        let dob = moment(deceased.dateOfBirth, 'DD/MM/YYYY');

        return Api.calculators.monetary.employmentContingencyDiscount({
            age: mSettleDate.diff(dob, 'years', true),
            disabled: deceased.disabledAtAccident,
            employment: deceased.employedAtDateOfDeath ? 'employed' : 'notEmployed',
            education: deceased.levelOfEducation,
            gender: deceased.gender,
        })
            .then((data) => {
                dispatch('updateTheDeceasedIncomeValue', {key: 'multiplier', value: data.multiplier});
            });

    }, 500),
};

// Mutations
const mutations = {
    [mutationTypes.UPDATE](state, {key, value}) {
        state[key] = value;
    },
    [mutationTypes.UPDATE_DECEASED](state, {key, value}) {
        state.theDeceased[key] = value;
    },
    [mutationTypes.UPDATE_DECEASED_LIFE_EXPECTANCY](state, {key, value}) {
        state.theDeceased.lifeExpectancy[key] = value;
    },
    [mutationTypes.UPDATE_DECEASED_INCOME](state, {key, value}) {
        state.theDeceased.income[key] = value;
    },
    [mutationTypes.SERVER_UPDATE_INCOME](state, data) {
        state.theDeceased.income = data;
        state.theDeceased.employedAtDateOfDeath = data.employedAtDateOfDeath;
        state.theDeceased.levelOfEducation = data.levelOfEducation;
        state.theDeceased.disabledAtAccident = data.disabledAtAccident;
    },
    [mutationTypes.ADD_DECEASED_INCOME_PERIOD](state, payload) {
        state.theDeceased.income.periods.push(payload);
        state.theDeceased.income.netPensionIncome += payload.netPensionIncome;
        state.theDeceased.income.grossPensionIncome += payload.grossPensionIncome;
        state.theDeceased.income.netIncome += payload.netIncome;
        state.theDeceased.income.grossIncome += payload.grossIncome;
    },
    [mutationTypes.UPDATE_DECEASED_INCOME_PERIOD](state, {key, value, index}) {
        if (~key.indexOf('Income')) {
            state.theDeceased.income[key] -= (state.theDeceased.income.periods[index][key] || 0);
        }
        state.theDeceased.income.periods[index][key] = value;
        if (~key.indexOf('Income')) {
            state.theDeceased.income[key] += (value || 0);
        }
    },
    [mutationTypes.DELETE_DECEASED_INCOME_PERIOD](state, payload) {
        state.theDeceased.income.periods.splice(payload, 1);
    },
    [mutationTypes.RESET](state) {
        state.theDeceased = personTemplate();
    },
    [mutationTypes.SERVER_UPDATE_DECEASED](state, data) {
        state.theDeceased.name = data.name;
        state.theDeceased.gender = data.gender;
        state.theDeceased.onsetOfSymptoms = data.onsetOfSymptoms;
        state.theDeceased.dateOfBirth = data.dateOfBirth;
        state.theDeceased.dateOfDeath = data.dateOfDeath;
        state.theDeceased.ageAtDeath = calcAgeDeath(state.theDeceased);
        state.theDeceased.lifeExpectancy = data.lifeExpectancy;
        state.theDeceased.income = data.income;
        state.theDeceased.levelOfEducation = data.levelOfEducation;
        state.theDeceased.disabledAtAccident = data.disabledAtAccident;
    },
    [mutationTypes.UPDATING_DECEASED_INCOME_PERIOD](state) {
        state.updatingDeceasedIncome = !state.updatingDeceasedIncome;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/fatal/dependency/deceased.js